import { useState } from 'react';
import { api } from '../lib/api';
import { useFetch } from '../lib/useFetch';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import type { PageProps } from '../App';
import type { PlanoConta } from '../types';

const NIVEL_LABEL: Record<number, string> = { 1: 'Classe', 2: 'Departamento', 3: 'Categoria' };
const FILHO_LABEL: Record<number, string> = { 1: 'departamento', 2: 'categoria' };

// Ordena a lista chata em hierarquia (classe → deptos → categorias).
function ordenar(items: PlanoConta[]): PlanoConta[] {
  const byParent = new Map<number | null, PlanoConta[]>();
  items.forEach((p) => {
    const k = p.parent_id ?? null;
    if (!byParent.has(k)) byParent.set(k, []);
    byParent.get(k)!.push(p);
  });
  for (const arr of byParent.values()) arr.sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome, 'pt-BR'));
  const out: PlanoConta[] = [];
  const walk = (parent: number | null) => {
    for (const node of byParent.get(parent) ?? []) { out.push(node); walk(node.id); }
  };
  walk(null);
  return out;
}

export function PlanoContas(_: PageProps) {
  const toast = useToast();
  const plano = useFetch(() => api.listPlano(), []);
  const items = plano.data?.items ?? [];
  const rows = ordenar(items);

  const [editId, setEditId] = useState<number | null>(null);
  const [editNome, setEditNome] = useState('');
  const [add, setAdd] = useState<{ parentId: number | null; nivel: number } | null>(null);
  const [merge, setMerge] = useState<PlanoConta | null>(null);

  const iniciarEdicao = (p: PlanoConta) => { setEditId(p.id); setEditNome(p.nome); };
  const salvarNome = async (p: PlanoConta) => {
    if (!editNome.trim() || editNome === p.nome) { setEditId(null); return; }
    try { await api.patchPlano(p.id, { nome: editNome.trim() }); setEditId(null); plano.refetch(); toast('Renomeado.'); }
    catch (e) { toast(errMsg(e, 'Falha ao renomear.'), 'err'); }
  };
  const toggle = async (p: PlanoConta) => {
    try { await api.patchPlano(p.id, { ativo: !p.ativo }); plano.refetch(); toast(p.ativo ? `“${p.nome}” desativada.` : `“${p.nome}” reativada.`); }
    catch (e) { toast(errMsg(e, 'Falha ao atualizar.'), 'err'); }
  };
  const excluir = async (p: PlanoConta) => {
    try { await api.excluirPlano(p.id); plano.refetch(); toast(`“${p.nome}” excluída.`); }
    catch (e) {
      const body = (e as { body?: { error?: string; lancamentos?: number; filhos?: number; fornecedores?: number } }).body;
      if (body?.error === 'em_uso') {
        toast(`Em uso (${body.lancamentos ?? 0} lançtos, ${body.filhos ?? 0} filhos). Desative ou mescle em vez de excluir.`, 'err');
      } else toast(errMsg(e, 'Falha ao excluir.'), 'err');
    }
  };

  return (
    <div className="page narrow">
      <div className="page-head">
        <h1>Plano de Contas</h1>
        <button className="btn dark" onClick={() => setAdd({ parentId: null, nivel: 1 })}>+ Nova classe</button>
      </div>
      <div className="subtitle">Classe → Departamento → Categoria (3 níveis fixos). Renomeie, adicione, desative, mescle ou exclua.</div>

      {plano.loading ? (
        <div className="loading">Carregando…</div>
      ) : (
        <div className="tbl">
          {rows.map((p) => (
            <div className="tbl-row" key={p.id} style={{ display: 'flex', gap: 10, padding: '9px 16px', alignItems: 'center' }}>
              {editId === p.id ? (
                <input className="input sm" style={{ maxWidth: 280, marginLeft: (p.nivel - 1) * 18 }} value={editNome} autoFocus
                  onChange={(e) => setEditNome(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') salvarNome(p); if (e.key === 'Escape') setEditId(null); }}
                  onBlur={() => salvarNome(p)} />
              ) : (
                <span style={{
                  fontSize: p.nivel === 1 ? 14 : 13,
                  fontWeight: p.nivel === 1 ? 700 : p.nivel === 2 ? 600 : 500,
                  color: p.nivel === 3 ? 'var(--text-2)' : 'var(--ink)',
                  paddingLeft: (p.nivel - 1) * 18,
                  opacity: p.ativo ? 1 : 0.5,
                }}>{p.nome}</span>
              )}
              <span className="chip-sq neutral" style={{ fontSize: 10 }}>{NIVEL_LABEL[p.nivel]}</span>
              {!!p.em_uso && <span className="text-sm muted">{p.em_uso} lançtos</span>}
              {!p.ativo && <span className="chip-sq neutral" style={{ fontSize: 10 }}>INATIVA</span>}
              <span className="spacer" />
              {p.nivel < 3 && <button className="btn link text-sm" style={{ color: 'var(--primary)' }} onClick={() => setAdd({ parentId: p.id, nivel: p.nivel + 1 })}>+ {FILHO_LABEL[p.nivel]}</button>}
              <button className="btn link text-sm" onClick={() => iniciarEdicao(p)}>renomear</button>
              <button className="btn link text-sm" onClick={() => setMerge(p)}>mesclar</button>
              <button className="btn link text-sm" onClick={() => toggle(p)}>{p.ativo ? 'desativar' : 'reativar'}</button>
              <button className="btn danger-link text-sm" onClick={() => excluir(p)}>excluir</button>
            </div>
          ))}
        </div>
      )}

      {add && <AddModal add={add} onClose={() => setAdd(null)} onDone={() => { setAdd(null); plano.refetch(); }} />}
      {merge && <MergeModal node={merge} irmaos={items.filter((x) => x.parent_id === merge.parent_id && x.id !== merge.id)} onClose={() => setMerge(null)} onDone={() => { setMerge(null); plano.refetch(); }} />}
    </div>
  );
}

function errMsg(e: unknown, fallback: string): string {
  const body = (e as { body?: { error?: string } }).body;
  if (body?.error === 'nome_duplicado') return 'Já existe um item com esse nome nesse nível.';
  if (body?.error === 'profundidade_maxima') return 'Máximo de 3 níveis atingido.';
  return fallback;
}

function AddModal({ add, onClose, onDone }: { add: { parentId: number | null; nivel: number }; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [nome, setNome] = useState('');
  const [busy, setBusy] = useState(false);
  const salvar = async () => {
    if (!nome.trim()) return;
    setBusy(true);
    try { await api.criarPlano({ nome: nome.trim(), parent_id: add.parentId }); toast(`${NIVEL_LABEL[add.nivel]} criada.`); onDone(); }
    catch (e) { toast(errMsg(e, 'Falha ao criar.'), 'err'); setBusy(false); }
  };
  return (
    <Modal title={`Nova ${NIVEL_LABEL[add.nivel].toLowerCase()}`} onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={salvar} disabled={busy || !nome.trim()}>Criar</button>
      </>}>
      <div className="field" style={{ marginBottom: 0 }}>
        <label>Nome</label>
        <input className="input" value={nome} autoFocus onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') salvar(); }} />
      </div>
    </Modal>
  );
}

function MergeModal({ node, irmaos, onClose, onDone }: { node: PlanoConta; irmaos: PlanoConta[]; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [destino, setDestino] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const confirmar = async () => {
    if (!destino) return;
    setBusy(true);
    try { await api.mesclarPlano(node.id, Number(destino)); toast('Mesclado.'); onDone(); }
    catch (e) { toast(errMsg(e, 'Falha ao mesclar.'), 'err'); setBusy(false); }
  };
  return (
    <Modal title={`Mesclar “${node.nome}”`} onClose={onClose}
      footer={<>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={confirmar} disabled={!destino || busy}>Mesclar</button>
      </>}>
      <div className="m-sub">Os lançamentos e filhos de <b>{node.nome}</b> passam para o item destino (mesmo nível), e este item é removido.</div>
      <label className="flabel">Destino</label>
      <select className="select" value={destino} onChange={(e) => setDestino(e.target.value ? Number(e.target.value) : '')}>
        <option value="">Escolher…</option>
        {irmaos.map((x) => <option key={x.id} value={x.id}>{x.nome}</option>)}
      </select>
      {irmaos.length === 0 && <div className="text-sm muted" style={{ marginTop: 8 }}>Não há outro item no mesmo nível para mesclar.</div>}
    </Modal>
  );
}
